import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { CheckSquare, Plus, Trash2, Clock, CalendarDays, Circle, CheckCircle2, X, Repeat, ListChecks, GripVertical, Pencil } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { TODO_COLORS } from "../../lib/todoColors";

const genId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
const loadFromDur = (m) => (m <= 20 ? 1 : m <= 40 ? 2 : m <= 60 ? 3 : m <= 90 ? 4 : 5);
const emptyForm = { title: "", notes: "", due_date: "", due_time: "", duration_min: 30, load: 0, color: TODO_COLORS[0].value, repeat: "none", subtasks: [] };

export default function Todos() {
  const [todos, setTodos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newSub, setNewSub] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/admin/todos").then((r) => setTodos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addSub = () => {
    const text = newSub.trim();
    if (!text) return;
    setForm((f) => ({ ...f, subtasks: [...f.subtasks, { id: genId(), text, done: false }] }));
    setNewSub("");
  };

  const reorderSub = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    setForm((f) => {
      const arr = [...f.subtasks];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(targetIdx, 0, moved);
      return { ...f, subtasks: arr };
    });
    setDragIdx(null);
  };

  const toggleSub = async (t, subId) => {
    const subtasks = (t.subtasks || []).map((st) => (st.id === subId ? { ...st, done: !st.done } : st));
    await api.put(`/admin/todos/${t.id}`, { subtasks });
    load();
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Geef de to-do een titel."); return; }
    setBusy(true);
    const payload = { title: form.title, notes: form.notes, due_date: form.due_date, due_time: form.due_time,
      duration_min: Number(form.duration_min) || 0, load: Number(form.load) || 0, color: form.color, repeat: form.repeat, subtasks: form.subtasks };
    try {
      if (editingId) { await api.put(`/admin/todos/${editingId}`, payload); toast.success("To-do bijgewerkt."); }
      else { await api.post("/admin/todos", payload); toast.success("To-do toegevoegd."); }
      setForm(emptyForm); setEditingId(null); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onEdit = (t) => {
    setForm({ title: t.title || "", notes: t.notes || "", due_date: t.due_date || "", due_time: t.due_time || "",
      duration_min: t.duration_min || 0, load: t.load || 0, color: t.color || TODO_COLORS[0].value, repeat: t.repeat || "none",
      subtasks: (t.subtasks || []).map((st) => ({ ...st })) });
    setEditingId(t.id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => { setShowForm(false); setForm(emptyForm); setEditingId(null); };

  const toggle = async (t) => {
    await api.put(`/admin/todos/${t.id}`, { done: !t.done });
    if (!t.done) toast.success(t.scheduled_start ? "To-do afgerond en uit agenda gehaald" : "To-do afgerond");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Deze to-do verwijderen?")) return;
    await api.delete(`/admin/todos/${id}`);
    toast.success("Verwijderd."); load();
  };

  const open = todos.filter((t) => !t.done);
  const done = todos.filter((t) => t.done);

  const dueTxt = (t) => {
    if (!t.due_date) return null;
    try {
      const d = parseISO(`${t.due_date}T${t.due_time || "00:00"}:00`);
      return format(d, t.due_time ? "EEE d MMM · HH:mm" : "EEE d MMM", { locale: nl });
    } catch { return t.due_date; }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="text-3xl font-medium tracking-tight">To-Do's</h1>
        <Button onClick={() => { if (!showForm) { setForm(emptyForm); setEditingId(null); } setShowForm((v) => !v); }} data-testid="todo-add-toggle"
          className="rounded-full bg-brand hover:bg-brand-hover text-white"><Plus className="w-4 h-4 mr-1" /> Nieuwe to-do</Button>
      </div>
      <p className="text-ink-muted text-sm mb-6">Beheer je eigen taken. Sleep ze op de agenda om er tijd voor te blokken.</p>

      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 mb-6 space-y-3" data-testid="todo-form">
          <h3 className="font-medium">{editingId ? "To-do bewerken" : "Nieuwe to-do"}</h3>
          <div>
            <Label className="text-sm mb-1 block">Titel *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="todo-title"
              placeholder="Bijv. voorraad bestellen" className="rounded-xl border-[#E8E2D9]" />
          </div>
          <div>
            <Label className="text-sm mb-1 block">Omschrijving</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="todo-notes"
              className="rounded-xl border-[#E8E2D9]" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-sm mb-1 block">Due-datum</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="todo-due-date" className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm mb-1 block">Tijd</Label>
              <Input type="time" step="900" value={form.due_time} onChange={(e) => setForm({ ...form, due_time: e.target.value })} data-testid="todo-due-time" className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm mb-1 block">Duur (min, optioneel)</Label>
              <Input type="number" min="0" step="15" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="todo-duration" className="rounded-xl border-[#E8E2D9]" /></div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Belasting (telt mee in de dagbelasting)</Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((v) => (
                <button key={v} type="button" data-testid={`todo-load-${v}`} onClick={() => setForm({ ...form, load: v })}
                  className={`w-9 h-9 rounded-lg border text-sm transition-colors ${((Number(form.load) || loadFromDur(Number(form.duration_min) || 30)) === v) ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{v}</button>
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-1">1 = licht · 3 = normaal · 5 = zwaar. De duur is optioneel; de belasting bepaalt hoe zwaar de taak op die dag meetelt (net als bij een privé-afspraak).</p>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Kleur</Label>
            <div className="flex gap-2">
              {TODO_COLORS.map((c) => (
                <button key={c.value} type="button" onClick={() => setForm({ ...form, color: c.value })} title={c.label}
                  data-testid={`todo-color-${c.value.replace('#','')}`}
                  className={`w-7 h-7 rounded-full border-2 transition-transform ${form.color === c.value ? "scale-110 border-ink" : "border-white"}`}
                  style={{ backgroundColor: c.value }} />
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Herhaling</Label>
            <select value={form.repeat} onChange={(e) => setForm({ ...form, repeat: e.target.value })} data-testid="todo-repeat"
              className="w-full rounded-xl border border-[#E8E2D9] p-2.5 text-sm bg-white">
              <option value="none">Eenmalig</option>
              <option value="weekly">Wekelijks</option>
              <option value="monthly">Maandelijks</option>
            </select>
            {form.repeat !== "none" && <p className="text-xs text-ink-muted mt-1">Zodra je deze afvinkt, komt de taak automatisch terug op de volgende {form.repeat === "weekly" ? "week" : "maand"} (op basis van de due-datum).</p>}
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Subtaken (stappen)</Label>
            {form.subtasks.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {form.subtasks.map((st, idx) => (
                  <div key={st.id} draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorderSub(idx)} onDragEnd={() => setDragIdx(null)}
                    data-testid={`form-subtask-${idx}`}
                    className={`flex items-center gap-2 rounded-lg px-1 py-0.5 ${dragIdx === idx ? "opacity-40" : ""} hover:bg-cream-subtle/50`}>
                    <GripVertical className="w-3.5 h-3.5 text-ink-muted cursor-grab active:cursor-grabbing shrink-0" />
                    <span className="text-sm flex-1">{st.text}</span>
                    <button type="button" onClick={() => setForm({ ...form, subtasks: form.subtasks.filter((x) => x.id !== st.id) })} className="text-ink-muted hover:text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                data-testid="todo-subtask-input" placeholder="Bijv. producten inpakken" className="rounded-xl border-[#E8E2D9]" />
              <Button type="button" variant="outline" onClick={addSub} data-testid="todo-subtask-add" className="rounded-xl border-[#E8E2D9] shrink-0"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={closeForm} className="rounded-full border-[#E8E2D9]">Annuleren</Button>
            <Button onClick={save} disabled={busy} data-testid="todo-save" className="rounded-full bg-brand hover:bg-brand-hover text-white">{busy ? "..." : editingId ? "Wijzigingen opslaan" : "Opslaan"}</Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="todo-open-list">
        {open.length === 0 && !showForm && (
          <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 text-center text-sm text-ink-muted">Geen open to-do's. Tijd voor koffie ☕</div>
        )}
        {open.map((t) => (
          <TodoRow key={t.id} t={t} dueTxt={dueTxt(t)} onToggle={() => toggle(t)} onRemove={() => remove(t.id)} onToggleSub={(id) => toggleSub(t, id)} onEdit={() => onEdit(t)} />
        ))}
      </div>

      {done.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm uppercase tracking-wide text-ink-muted mb-2">Afgerond</h2>
          <div className="space-y-2" data-testid="todo-done-list">
            {done.map((t) => (
              <TodoRow key={t.id} t={t} dueTxt={dueTxt(t)} onToggle={() => toggle(t)} onRemove={() => remove(t.id)} onToggleSub={(id) => toggleSub(t, id)} onEdit={() => onEdit(t)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TodoRow({ t, dueTxt, onToggle, onRemove, onToggleSub, onEdit }) {
  const subs = t.subtasks || [];
  const subsDone = subs.filter((s) => s.done).length;
  return (
    <div className={`bg-white rounded-xl border border-[#E8E2D9] p-3 flex items-start gap-3 group ${t.done ? "opacity-60" : ""}`} data-testid={`todo-row-${t.id}`}>
      <button onClick={onToggle} data-testid={`todo-toggle-${t.id}`} className="mt-0.5 shrink-0 text-brand">
        {t.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5 text-ink-muted hover:text-brand" />}
      </button>
      <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: t.color }} />
      <div className="min-w-0 flex-1">
        <div className={`font-medium ${t.done ? "line-through" : ""}`}>{t.title}</div>
        {t.notes && <div className="text-sm text-ink-muted">{t.notes}</div>}
        <div className="flex flex-wrap gap-3 mt-1 text-xs text-ink-muted">
          {dueTxt && <span className="inline-flex items-center gap-1 capitalize"><CalendarDays className="w-3 h-3" /> {dueTxt}</span>}
          {t.duration_min > 0 && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.duration_min} min</span>}
          <span className="inline-flex items-center gap-1" title="Belasting (telt mee in de dagbelasting)">Belasting {(t.load || 0) || loadFromDur(t.duration_min || 30)}</span>
          {subs.length > 0 && <span className="inline-flex items-center gap-1"><ListChecks className="w-3 h-3" /> {subsDone}/{subs.length}</span>}
          {t.repeat && t.repeat !== "none" && <span className="inline-flex items-center gap-1 text-brand"><Repeat className="w-3 h-3" /> {t.repeat === "weekly" ? "Wekelijks" : "Maandelijks"}</span>}
          {t.scheduled_start && <span className="inline-flex items-center gap-1 text-brand"><CheckSquare className="w-3 h-3" /> Op agenda</span>}
        </div>
        {subs.length > 0 && (
          <div className="mt-2 space-y-1" data-testid={`todo-subtasks-${t.id}`}>
            {subs.map((st) => (
              <button key={st.id} onClick={() => onToggleSub(st.id)} data-testid={`subtask-${st.id}`}
                className="flex items-center gap-2 text-left w-full group/sub">
                {st.done ? <CheckCircle2 className="w-4 h-4 text-brand shrink-0" /> : <Circle className="w-4 h-4 text-ink-muted group-hover/sub:text-brand shrink-0" />}
                <span className={`text-sm ${st.done ? "line-through text-ink-muted" : ""}`}>{st.text}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button onClick={onEdit} data-testid={`todo-edit-${t.id}`} className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-brand transition-opacity">
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={onRemove} data-testid={`todo-remove-${t.id}`} className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-destructive transition-opacity">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
